import { z } from "zod";

// Phase 7 WIDG-11 typed configSchema for the `branch-variant` template
// (D-10..D-13). Replaces the Phase 01.1 z.unknown() placeholder in
// TEMPLATE_CATALOG so that validateRecipe Pass 2A enforces the per-widget
// config shape end-to-end. (Plan 05 swaps this schema into TEMPLATE_CATALOG;
// this file ships pure schema.)
//
// D-13 cross-widget composition: BranchVariantConfig carries
// `sourceOptionSetWidgetId` referencing an option-set widget instance in the
// same recipe (mirrors ranked-tier's `optionSetWidgetId` shape). The renderer
// (Plan 10) consumes source state via `useOtherWidgetState(widgetId)` to know
// which options are selected; this schema only carries structural data.
// Widget state — `{ selectedVariantByOptionId: Record<OptionId, VariantId> }`
// per D-13 — is not encoded by the schema.
//
// D-10: variants[] is a flat list of `{ id, label, attachToOptionIds[],
// overrideFields }`. Attachment is by option-id list (one variant can attach
// to multiple options). No nested-inside-option-set hierarchy — preserves
// template decoupling.
//
// D-11: `overrideFields` is `z.record(z.string(), z.unknown())` at the catalog
// level — recipes never narrow the value type at this layer. Recipe-level
// `recipe.variantOverrideShapes: Record<string, ZodSchema>` plus the
// superRefine binding that pairs them is Plan 06's recipe-root-walk
// responsibility per RESEARCH §"Primary Recommendation 4". The catalog stays
// generic.
//
// RESEARCH §"Anti-Patterns to Avoid" (line 626): use `z.unknown()` for the
// override value type — Zod's permissive `any` constructor is unsound
// (escapes all type narrowing) and is explicitly disallowed here. The
// structural defense is the W-8 grep gate in Plan 04's `<verify>` block,
// which fails the build if the disallowed call form appears anywhere in this
// file.
//
// Cross-widget existence-check (does `sourceOptionSetWidgetId` point to an
// existing option-set widget? do `attachToOptionIds` exist on that source
// widget?) is NOT enforced here — that requires a recipe-root walk
// (future Pass 2B, out of Plan 04 scope). The renderer fails soft when the
// source widget is missing per D-12 + threat T-07-04-06.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15 + Phase 6 D-09): export the interfaces explicitly and
// annotate the const with z.ZodType<BranchVariantConfig> so tsc can emit
// declarations without reading across module boundaries (no TS9013).
// `Readonly<Record<string, unknown>>` on overrideFields preserves immutability
// for downstream consumers (Phase 01.1 D-10 + Phase 6 D-09 style).

export interface BranchVariant {
  id: string;
  label: string;
  attachToOptionIds: ReadonlyArray<string>;
  overrideFields: Readonly<Record<string, unknown>>;
}

export interface BranchVariantConfig {
  sourceOptionSetWidgetId: string;
  variants: ReadonlyArray<BranchVariant>;
}

// Per-variant strictObject — kebab-case id regex matches the option-set
// `_optionEntrySchema` pattern so id spaces are consistent across widget
// templates. `attachToOptionIds.min(1)` per D-10 — a variant attaching to zero
// options is meaningless. `overrideFields: z.record(z.string(), z.unknown())`
// is NOT optional: an empty `{}` is a valid record value and the renderer
// (Plan 10) handles the empty-record case with no `<dl>` rendering
// (UI-SPEC line 277).
const _variantSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  attachToOptionIds: z.array(z.string().min(1)).min(1),
  overrideFields: z.record(z.string(), z.unknown()),
});

// Top-level schema: variants.min(1) — a branch-variant widget with zero
// variants is meaningless; variants.max(50) — a soft cap bounding renderer
// complexity. 50 is generous (no real recipe should approach this) but past
// 50 the inline-card rendering becomes a scroll wall (UI-SPEC §"Branch-Variant
// Visual Contract"). superRefine emits a per-index issue for duplicate
// variant ids, mirroring summary-checklist's nested-arrays multi-issue
// pattern (one ctx.addIssue per offence; tracking via Set keeps the loop
// O(n)).
export const BranchVariantConfigSchema: z.ZodType<BranchVariantConfig> = z
  .strictObject({
    sourceOptionSetWidgetId: z.string().min(1),
    variants: z.array(_variantSchema).min(1).max(50),
  })
  .superRefine((cfg, ctx) => {
    const seenIds = new Set<string>();
    for (const [i, v] of cfg.variants.entries()) {
      if (seenIds.has(v.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["variants", i, "id"],
          message: `duplicate variant id "${v.id}"`,
        });
      }
      seenIds.add(v.id);
    }
  });
